import { demoStages, type DemoStageDefinition, type DemoStageId } from './demoExperience';

/**
 * MRM 2.24.3 — the guided tour runs on the app's own screens.
 *
 * Until 2.24.2 the eight-stage tour drew its own scene cards, its own progress
 * bar and its own controller: a second app that happened to look like this
 * one. Every fix to a real screen had to be made twice, and twice it was not.
 *
 * Now each stage is a *stop*: a real route plus the sentence that explains why
 * a visitor is looking at it. The tour navigates; the app renders.
 */
export interface DemoTourStop extends DemoStageDefinition {
  /** A real app route. Never a demo-only screen. */
  route: string;
  /** What to look at on that screen, in one sentence. */
  focus: string;
}

const ROUTES: Record<DemoStageId, { route: string; focus: string }> = {
  'first-snow': { route: '/child/home', focus: '아이가 매일 처음 보는 화면이에요. 오늘 할 일 하나만 크게 보입니다.' },
  'goal-road': { route: '/village/hub', focus: '목표는 남과 비교하는 순위가 아니라 내 마을이 자라는 모습으로 보여요.' },
  basecamp: { route: '/child/records', focus: '받고·쓰고·모은 돈을 여기에 남깁니다. 금액보다 이유를 함께 적어요.' },
  weather: { route: '/today/article', focus: '오늘의 경제 기사와 퀴즈입니다. 뉴스를 날씨처럼 읽는 연습이에요.' },
  library: { route: '/child/concepts', focus: '개념을 외우게 하지 않고 이야기와 오늘의 선택에 연결합니다.' },
  fridge: { route: '/child/fridge', focus: '오늘의 노력과 가족의 응원이 냥장고에 기억으로 남습니다.' },
  family: { route: '/child/family-expedition', focus: '가족의 네 가지 신호가 모이면 아이의 이글루와 퍼즐이 함께 자라요.' },
  summit: { route: '/child/hall-of-honor', focus: '점수 대신 달라진 선택이 수료증과 배지로 남습니다.' },
};

export const DEMO_TOUR_STOPS: DemoTourStop[] = demoStages.map((stage) => ({ ...stage, ...ROUTES[stage.id] }));

/** Where the tour ends: the report reads the choices made along the way. */
export const DEMO_TOUR_EXIT = '/demo/report';

export function tourStopAt(index: number): DemoTourStop {
  const bounded = Math.max(0, Math.min(DEMO_TOUR_STOPS.length - 1, index));
  return DEMO_TOUR_STOPS[bounded];
}

/**
 * The tour is on when a guided session is running. `guided` has meant exactly
 * this since 2.5 — the entry screen's 기관·파트너 소개 route — so there is no
 * second flag to keep in step with this one.
 */
export function tourIsRunning(session: { started: boolean; mode: string }): boolean {
  return session.started && session.mode === 'guided';
}

/** The tour narrates app screens; on its own demo screens it stays out of the way. */
export function tourAppliesTo(pathname: string): boolean {
  return !pathname.startsWith('/demo');
}
